import React from 'react';
import { Card, Form, Button, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faClock,
    faSave
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useCriarPrazos } from 'hooks/usePrazo';
import { CriarPrazosDto } from 'services/prazoService';
import { validateQuantidadeParcelas } from 'data/prazos';
import PageBreadcrumb from "components/common/PageBreadcrumb";
import { useToast } from "providers/ToastProvider";

// Define breadcrumbs for the new prazo page
const prazoBreadcrumbItems = () => [
    {
        label: "Retaguarda",
        url: "#!",
    },
    {
        label: "Cadastros",
        url: "#!",
    },
    {
        label: "Prazos",
        url: "/cadastros/prazos",
    },
    {
        label: "Novo",
        active: true,
    },
];

const NovoPrazo: React.FC = () => {
    const navigate = useNavigate();
    const { showToast } = useToast();

    // React Hook Form setup
    const {
        register,
        handleSubmit,
        formState: { errors },
        watch,
    } = useForm<CriarPrazosDto>({
        defaultValues: {
            quantidadeParcelas: 1,
        },
        mode: 'onSubmit',
    });

    // Hooks
    const criarMutation = useCriarPrazos();

    // Watch field for real-time preview
    const quantidadeParcelas = watch('quantidadeParcelas');

    const onSubmit = async (data: CriarPrazosDto) => {
        try {
            const validation = validateQuantidadeParcelas(data.quantidadeParcelas);
            if (!validation.isValid) {
                showToast(validation.errorMessage!, 'error');
                return;
            }

            const result = await criarMutation.mutateAsync(data);

            // Navigate with success message
            navigate('/cadastros/prazos');
            showToast(`${result.prazosIds.length} prazo(s) criado(s) com sucesso`, 'success');
        } catch (error) {
            console.error('Erro ao criar prazos:', error);
        }
    };

    // Generate preview of prazos that will be created
    const generatePreview = (quantidade: number) => {
        if (!quantidade || quantidade <= 0) return [];

        const prazos = [];
        for (let i = 1; i <= Math.min(quantidade, 99); i++) {
            prazos.push({
                parcela: i,
                descricao: i === 1 ? "À Vista" : `${i}x`
            });
        }
        return prazos;
    };

    const previewPrazos = generatePreview(quantidadeParcelas);

    return (
        <div>
            <PageBreadcrumb items={prazoBreadcrumbItems()} />

            <Form
                noValidate
                className="mb-9"
                onSubmit={handleSubmit(onSubmit)}
            >
                <div className="d-flex flex-wrap gap-3 flex-between-end mb-5">
                    <div>
                        <h2 className="mb-2">Cadastrar Prazos</h2>
                        <h5 className="text-body-tertiary fw-semibold">
                            Informe a quantidade de parcelas para gerar os prazos automaticamente
                        </h5>
                    </div>
                    <div className="d-flex flex-wrap gap-2">
                        <Button
                            variant="primary"
                            type="submit"
                            disabled={criarMutation.isPending}
                        >
                            <FontAwesomeIcon icon={faSave} className="me-2" />
                            {criarMutation.isPending ? "Criando..." : "Criar Prazos"}
                        </Button>
                    </div>
                </div>

                {/* Configuração Card */}
                <Card className="mb-4">
                    <Card.Body>
                        <h5 className="mb-3">
                            <FontAwesomeIcon icon={faClock} className="me-2" />
                            Configuração
                        </h5>

                        {criarMutation.isError && (
                            <Alert variant="danger" className="border border-danger border-opacity-25 bg-danger-subtle mb-4">
                                <div className="text-danger-emphasis">
                                    <strong>Erro:</strong> Não foi possível criar os prazos.
                                    Tente novamente.
                                </div>
                            </Alert>
                        )}

                        <div className="row g-3">
                            {/* Quantidade de Parcelas */}
                            <div className="col-md-6">
                                <Form.Group>
                                    <Form.Label className="text-body-emphasis fw-semibold">
                                        Quantidade de Parcelas *
                                    </Form.Label>
                                    <Form.Control
                                        type="number"
                                        min="1"
                                        max="99"
                                        {...register('quantidadeParcelas', {
                                            required: 'Quantidade de parcelas é obrigatória',
                                            min: {
                                                value: 1,
                                                message: 'Quantidade deve ser pelo menos 1'
                                            },
                                            max: {
                                                value: 99,
                                                message: 'Quantidade não pode ser maior que 99'
                                            },
                                            setValueAs: (value) => parseInt(value) || 0
                                        })}
                                        isInvalid={!!errors.quantidadeParcelas}
                                        placeholder="Ex: 12"
                                        autoFocus
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors.quantidadeParcelas?.message}
                                    </Form.Control.Feedback>
                                    <Form.Text className="text-body-secondary">
                                        Será criado um prazo para cada parcela (1x até {quantidadeParcelas || 1}x)
                                    </Form.Text>
                                </Form.Group>
                            </div>
                        </div>
                    </Card.Body>
                </Card>

                {/* Preview Card */}
                {previewPrazos.length > 0 && (
                    <Card className="mb-4">
                        <Card.Body>
                            <h5 className="mb-3">
                                <FontAwesomeIcon icon={faClock} className="me-2" />
                                Prévia dos Prazos ({previewPrazos.length} prazo{previewPrazos.length !== 1 ? 's' : ''})
                            </h5>

                            <div className="row g-2">
                                {previewPrazos.map((prazo, index) => (
                                    <div key={index} className="col-md-3 col-sm-4 col-6">
                                        <div className="border rounded p-2 text-center bg-body-secondary bg-opacity-25">
                                            <div className="fw-semibold text-primary-emphasis">
                                                {prazo.parcela}x
                                            </div>
                                            <div className="text-body-secondary small">
                                                {prazo.descricao}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <Alert variant="info" className="border border-info border-opacity-25 bg-info-subtle mt-3 mb-0">
                                <div className="text-info-emphasis">
                                    <FontAwesomeIcon icon={faClock} className="me-2" />
                                    <strong>Observação:</strong> Prazos que já existem não serão duplicados. Apenas novos prazos serão criados.
                                </div>
                            </Alert>
                        </Card.Body>
                    </Card>
                )}

                <div className="d-flex justify-content-between mt-4">
                    <Button
                        variant="secondary"
                        type="button"
                        onClick={() => navigate("/cadastros/prazos")}
                    >
                        Cancelar
                    </Button>
                    <Button
                        variant="primary"
                        type="submit"
                        disabled={criarMutation.isPending || previewPrazos.length === 0}
                    >
                        <FontAwesomeIcon icon={faSave} className="me-2" />
                        {criarMutation.isPending ? "Criando..." : `Criar ${previewPrazos.length} Prazo${previewPrazos.length !== 1 ? 's' : ''}`}
                    </Button>
                </div>
            </Form>
        </div>
    );
};

export default NovoPrazo;